import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryService from '../../../services/nursery';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { useOrgTimezone, getTodayInTz } from '../../../utils/timezone';
import { money, pct, round2 } from '../../../utils/nurseryMoney';
import type { NurseryCustomer } from '../../../types/nursery';
import {
  REBATE_KIND_LABELS,
  REBATE_SETTLEMENT_LABELS,
  type CreateRebateAgreementPayload,
  type RebateAgreement,
  type RebateBandMode,
  type RebateKind,
  type RebatePeriod,
  type RebateSettlement,
} from '../../../types/nurseryAccounts';

/**
 * A new rebate agreement. There is no edit form on purpose: an agreement is
 * ended and replaced, never changed, so the settlement of an old period can
 * always be worked out again from the agreement it was made under.
 *
 * What each kind needs mirrors `agreementRefusal` in the server's
 * nurseryRebateService; the server stays the authority and its refusal is
 * shown in the dialog.
 */

export interface RebateAgreementDialogProps {
  initialCustomerId?: number;
  onClose: () => void;
  onCreated: (a: RebateAgreement) => void;
}

interface PartRow { key: number; label: string; rate: string; amount: string; account: string }
interface BandRow { key: number; threshold: string; rate: string }

const KINDS: RebateKind[] = ['flat_pct', 'volume_tiered', 'fixed_amount', 'settlement_discount'];

/** Which ways each kind can be settled; the first is the default. */
const SETTLEMENTS: Record<RebateKind, RebateSettlement[]> = {
  flat_pct: ['per_invoice', 'credit_note', 'billed_to_us'],
  volume_tiered: ['per_invoice', 'credit_note', 'billed_to_us'],
  fixed_amount: ['credit_note', 'billed_to_us'],
  settlement_discount: [],
};

const SETTLEMENT_HELP: Record<RebateSettlement, string> = {
  per_invoice: 'A credit note is raised with every invoice, so the amount owed on the invoice is exactly what the customer pays.',
  credit_note: 'One credit note is raised on the account when the period is closed.',
  billed_to_us: 'The customer sends its own invoice or claim. Nothing is credited here; closing the period records what was earned.',
};

const BAND_MODE_HELP: Record<RebateBandMode, string> = {
  retrospective: 'The band reached applies to all spend in the period.',
  marginal: 'Each band applies only to the spend above it.',
};

const PERIOD_LABELS: Record<RebatePeriod, string> = { month: 'Each month', quarter: 'Each quarter', year: 'Each year' };

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const INPUT = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const LABEL = 'block text-sm font-medium text-neutral-700 mb-1';
const SMALL_LABEL = 'block text-xs font-medium text-neutral-600 mb-1';

/** A typed number, or null when the box is blank or not a number. */
const typed = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export function RebateAgreementDialog({ initialCustomerId, onClose, onCreated }: RebateAgreementDialogProps) {
  const tz = useOrgTimezone();
  const nextKey = useRef(10);
  const newKey = () => { nextKey.current += 1; return nextKey.current; };

  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number>(initialCustomerId ?? 0);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<RebateKind>('flat_pct');
  const [settlement, setSettlement] = useState<RebateSettlement>('per_invoice');
  const [parts, setParts] = useState<PartRow[]>([
    { key: 1, label: 'Rebate', rate: '', amount: '', account: '' },
    { key: 2, label: 'Marketing', rate: '', amount: '', account: '' },
  ]);
  const [bands, setBands] = useState<BandRow[]>([{ key: 3, threshold: '0', rate: '' }]);
  const [bandMode, setBandMode] = useState<RebateBandMode>('retrospective');
  const [accountCode, setAccountCode] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [discountDays, setDiscountDays] = useState('');
  const [period, setPeriod] = useState<RebatePeriod>('year');
  const [startMonth, setStartMonth] = useState(7);
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [from, setFrom] = useState(() => getTodayInTz(tz));
  const [to, setTo] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await nurseryService.getCustomers({ top_level: true, active: true });
        if (!live) return;
        if (res.success) setCustomers(res.data || []);
        else setCustomersError(errorMessage(res, 'Could not load customers'));
      } catch (e) {
        if (live) setCustomersError(errorMessage(e, 'Could not load customers'));
      }
    })();
    return () => { live = false; };
  }, []);

  const changeKind = (k: RebateKind) => {
    setKind(k);
    setError(null);
    const options = SETTLEMENTS[k];
    if (options.length && !options.includes(settlement)) setSettlement(options[0]);
    if (k === 'fixed_amount' && parts.every((p) => !p.amount)) {
      setParts([{ key: newKey(), label: 'Marketing contribution', rate: '', amount: '', account: '' }]);
    }
    if (k === 'flat_pct' && parts.every((p) => !p.rate)) {
      setParts([
        { key: newKey(), label: 'Rebate', rate: '', amount: '', account: '' },
        { key: newKey(), label: 'Marketing', rate: '', amount: '', account: '' },
      ]);
    }
  };

  const setPart = (key: number, patch: Partial<PartRow>) =>
    setParts((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const setBand = (key: number, patch: Partial<BandRow>) =>
    setBands((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const totalRate = round2(parts.reduce((n, p) => n + (typed(p.rate) ?? 0), 0));
  const totalAmount = round2(parts.reduce((n, p) => n + (typed(p.amount) ?? 0), 0));

  /** The payload, or the sentence saying what is still missing. */
  const build = (): { payload?: CreateRebateAgreementPayload; problem?: string } => {
    if (!customerId) return { problem: 'Choose the customer this agreement is with.' };
    if (name.trim().length < 2) return { problem: 'Give the agreement a name.' };

    let components: CreateRebateAgreementPayload['components'] = [];
    let bandList: CreateRebateAgreementPayload['bands'] = [];

    if (kind === 'flat_pct' || kind === 'fixed_amount') {
      const field = kind === 'flat_pct' ? 'rate' : 'amount';
      // A row left wholly blank is a row nobody wanted.
      const used = parts.filter((p) => p[field].trim() !== '' || p.account.trim() !== '');
      for (const p of used) {
        const v = typed(p[field]);
        if (v === null || v < 0) {
          return { problem: kind === 'flat_pct' ? 'Each part needs a rate, as a number.' : 'Each part needs an amount, as a number.' };
        }
        if (kind === 'flat_pct' && v > 100) return { problem: 'A rate cannot be more than 100%.' };
        if (!p.label.trim()) return { problem: 'Each part needs a label, such as Rebate or Marketing.' };
      }
      if (!used.some((p) => (typed(p[field]) ?? 0) > 0)) {
        return {
          problem: kind === 'flat_pct'
            ? 'Give at least one part a rate above zero.'
            : 'Give at least one part an amount per period above zero.',
        };
      }
      components = used.map((p) => ({
        label: p.label.trim(),
        rate_pct: kind === 'flat_pct' ? typed(p.rate) : null,
        fixed_amount: kind === 'fixed_amount' ? typed(p.amount) : null,
        account_code: p.account.trim() || null,
      }));
    }

    if (kind === 'volume_tiered') {
      const used = bands.filter((b) => b.rate.trim() !== '');
      if (used.length === 0) return { problem: 'Add at least one spend band with a rate.' };
      const seen = new Set<number>();
      for (const b of used) {
        const threshold = typed(b.threshold);
        const rate = typed(b.rate);
        if (threshold === null || threshold < 0) return { problem: 'Each band needs the spend it starts from, as a number.' };
        if (rate === null || rate < 0 || rate > 100) return { problem: 'Each band needs a rate between 0 and 100%.' };
        if (seen.has(threshold)) return { problem: `Two bands start at ${money(threshold)}. Each band needs its own starting spend.` };
        seen.add(threshold);
        bandList.push({ threshold, rate_pct: rate });
      }
      bandList = bandList.sort((a, b) => a.threshold - b.threshold);
    }

    let days: number | null = null;
    if (kind === 'settlement_discount') {
      const rate = typed(discountRate);
      if (rate === null || rate <= 0 || rate > 100) return { problem: 'Give the discount a rate above zero.' };
      days = typed(discountDays);
      if (days === null || !Number.isInteger(days) || days < 1 || days > 365) {
        return { problem: 'Say within how many days the invoice must be paid to earn the discount.' };
      }
      components = [{ label: 'Settlement discount', rate_pct: rate, fixed_amount: null, account_code: accountCode.trim() || null }];
    }

    if (!from) return { problem: 'Say when the agreement starts.' };
    if (to && to < from) return { problem: 'The agreement cannot end before it starts.' };

    return {
      payload: {
        customer_id: customerId,
        name: name.trim(),
        kind,
        ...(kind === 'settlement_discount' ? {} : { settlement }),
        period,
        period_start_month: startMonth,
        band_mode: bandMode,
        include_delivery: includeDelivery,
        discount_days: days,
        account_code: kind === 'volume_tiered' || kind === 'settlement_discount' ? accountCode.trim() || null : null,
        effective_from: from,
        effective_to: to || null,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        components,
        bands: bandList,
      },
    };
  };

  const submit = async () => {
    const { payload, problem } = build();
    if (!payload) {
      setError(problem ?? 'Something is missing.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.createRebateAgreement(payload);
      if (res.success && res.data) onCreated(res.data);
      else {
        setError(errorMessage(res, 'Could not save the agreement'));
        setBusy(false);
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not save the agreement'));
      setBusy(false);
    }
  };

  const settlements = SETTLEMENTS[kind];
  const isRate = kind === 'flat_pct';

  return (
    <Dialog
      title="New rebate agreement"
      subtitle="An agreement is ended and replaced, never changed, so check it before you save."
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save agreement'}</Button>
        </>
      }
    >
      <div>
        <label htmlFor="ra-customer" className={LABEL}>Customer</label>
        <select id="ra-customer" value={customerId || ''} onChange={(e) => setCustomerId(Number(e.target.value) || 0)} className={INPUT}>
          <option value="">Choose a customer…</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.customer_code ? ` (${c.customer_code})` : ''}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-neutral-500">Rebates belong to whoever pays, so they go on the head office.</p>
        {customersError && <p className="mt-1 text-xs text-red-700 break-words">{customersError}</p>}
      </div>

      <div>
        <label htmlFor="ra-name" className={LABEL}>Name</label>
        <input id="ra-name" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={255}
          placeholder="Trading terms 2026–27" className={INPUT} />
      </div>

      <div>
        <label htmlFor="ra-kind" className={LABEL}>What the customer gets back</label>
        <select id="ra-kind" value={kind} onChange={(e) => changeKind(e.target.value as RebateKind)} className={INPUT}>
          {KINDS.map((k) => <option key={k} value={k}>{REBATE_KIND_LABELS[k]}</option>)}
        </select>
      </div>

      {settlements.length > 0 && (
        <fieldset>
          <legend className={LABEL}>How it is settled</legend>
          <div className="space-y-2">
            {settlements.map((s) => (
              <label key={s} htmlFor={`ra-settlement-${s}`} className="flex items-start gap-2 text-sm text-neutral-800">
                <input id={`ra-settlement-${s}`} type="radio" name="ra-settlement" className="mt-1 flex-shrink-0"
                  checked={settlement === s} onChange={() => setSettlement(s)} />
                <span className="min-w-0 break-words">
                  <span className="font-medium">{REBATE_SETTLEMENT_LABELS[s]}</span>
                  <span className="block text-xs text-neutral-500">{SETTLEMENT_HELP[s]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {(kind === 'flat_pct' || kind === 'fixed_amount') && (
        <div>
          <p className={LABEL}>Parts</p>
          <ul className="space-y-2">
            {parts.map((p) => (
              <li key={p.key} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_7rem_8rem_auto] sm:items-end">
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <label htmlFor={`ra-part-label-${p.key}`} className={SMALL_LABEL}>Label</label>
                  <input id={`ra-part-label-${p.key}`} type="text" value={p.label} maxLength={100}
                    onChange={(e) => setPart(p.key, { label: e.target.value })} className={INPUT} />
                </div>
                <div className="min-w-0">
                  <label htmlFor={`ra-part-value-${p.key}`} className={SMALL_LABEL}>{isRate ? 'Rate %' : 'Amount per period $'}</label>
                  <input id={`ra-part-value-${p.key}`} type="number" inputMode="decimal" min={0} step="0.01"
                    value={isRate ? p.rate : p.amount}
                    onChange={(e) => setPart(p.key, isRate ? { rate: e.target.value } : { amount: e.target.value })}
                    className={INPUT} />
                </div>
                <div className="min-w-0">
                  <label htmlFor={`ra-part-account-${p.key}`} className={SMALL_LABEL}>Xero account code</label>
                  <input id={`ra-part-account-${p.key}`} type="text" value={p.account} maxLength={20} placeholder="Optional"
                    onChange={(e) => setPart(p.key, { account: e.target.value })} className={INPUT} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button type="button" onClick={() => setParts((rows) => rows.filter((r) => r.key !== p.key))}
                    disabled={parts.length === 1} aria-label={`Remove ${p.label || 'this part'}`}
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm text-neutral-600 hover:bg-neutral-100 disabled:opacity-40">
                    <Trash2 className="h-4 w-4" /> <span className="sm:hidden">Remove</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <Button variant="outline" size="sm" disabled={parts.length >= 10}
              onClick={() => setParts((rows) => [...rows, { key: newKey(), label: '', rate: '', amount: '', account: '' }])}>
              <Plus className="h-4 w-4 mr-1" /> Add a part
            </Button>
            <p className="text-sm font-medium text-neutral-800">
              {isRate ? `${pct(totalRate)} in all` : `${money(totalAmount)} each period in all`}
            </p>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            {isRate
              ? 'Each part is credited as its own line, with GST.'
              : 'Amounts are before GST. Each part is credited as its own line, with GST, when the period is closed.'}
            {' '}A part with no account code goes to the rebate account chosen on the Xero page.
          </p>
        </div>
      )}

      {kind === 'volume_tiered' && (
        <>
          <div>
            <p className={LABEL}>Spend bands</p>
            <ul className="space-y-2">
              {bands.map((b) => (
                <li key={b.key} className="border border-neutral-200 rounded-lg p-3 grid grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                  <div className="min-w-0">
                    <label htmlFor={`ra-band-from-${b.key}`} className={SMALL_LABEL}>Spend from $</label>
                    <input id={`ra-band-from-${b.key}`} type="number" inputMode="decimal" min={0} step="1" value={b.threshold}
                      onChange={(e) => setBand(b.key, { threshold: e.target.value })} className={INPUT} />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor={`ra-band-rate-${b.key}`} className={SMALL_LABEL}>Rate %</label>
                    <input id={`ra-band-rate-${b.key}`} type="number" inputMode="decimal" min={0} max={100} step="0.01" value={b.rate}
                      onChange={(e) => setBand(b.key, { rate: e.target.value })} className={INPUT} />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <button type="button" onClick={() => setBands((rows) => rows.filter((r) => r.key !== b.key))}
                      disabled={bands.length === 1} aria-label="Remove this band"
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-sm text-neutral-600 hover:bg-neutral-100 disabled:opacity-40">
                      <Trash2 className="h-4 w-4" /> <span className="sm:hidden">Remove</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-2">
              <Button variant="outline" size="sm" disabled={bands.length >= 20}
                onClick={() => setBands((rows) => [...rows, { key: newKey(), threshold: '', rate: '' }])}>
                <Plus className="h-4 w-4 mr-1" /> Add a band
              </Button>
            </div>
            <p className="mt-2 text-xs text-neutral-500">Spend is what was invoiced in the period, before GST, less credit notes.</p>
          </div>

          <fieldset>
            <legend className={LABEL}>What a band applies to</legend>
            <div className="space-y-2">
              {(['retrospective', 'marginal'] as RebateBandMode[]).map((m) => (
                <label key={m} htmlFor={`ra-band-mode-${m}`} className="flex items-start gap-2 text-sm text-neutral-800">
                  <input id={`ra-band-mode-${m}`} type="radio" name="ra-band-mode" className="mt-1 flex-shrink-0"
                    checked={bandMode === m} onChange={() => setBandMode(m)} />
                  <span className="min-w-0 break-words">
                    <span className="font-medium">{m === 'retrospective' ? 'All spend' : 'Only the spend above it'}</span>
                    <span className="block text-xs text-neutral-500">{BAND_MODE_HELP[m]}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {settlement === 'per_invoice' && (
            <p className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-900 break-words">
              Each invoice is credited at the band reached so far. Closing the period works out the band actually
              reached and credits the difference.
            </p>
          )}
        </>
      )}

      {kind === 'settlement_discount' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="ra-discount-rate" className={LABEL}>Discount %</label>
            <input id="ra-discount-rate" type="number" inputMode="decimal" min={0} max={100} step="0.01" value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)} className={INPUT} />
          </div>
          <div>
            <label htmlFor="ra-discount-days" className={LABEL}>If paid within this many days</label>
            <input id="ra-discount-days" type="number" inputMode="numeric" min={1} max={365} step="1" value={discountDays}
              onChange={(e) => setDiscountDays(e.target.value)} className={INPUT} />
          </div>
          <p className="sm:col-span-2 text-xs text-neutral-500">
            The discount is taken when the customer pays, so there is no period to close.
          </p>
        </div>
      )}

      {(kind === 'volume_tiered' || kind === 'settlement_discount') && (
        <div>
          <label htmlFor="ra-account" className={LABEL}>Xero account code</label>
          <input id="ra-account" type="text" value={accountCode} maxLength={20} placeholder="Optional"
            onChange={(e) => setAccountCode(e.target.value)} className={INPUT} />
          <p className="mt-1 text-xs text-neutral-500">Left blank, it goes to the rebate account chosen on the Xero page.</p>
        </div>
      )}

      {kind !== 'settlement_discount' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="ra-period" className={LABEL}>Worked out</label>
            <select id="ra-period" value={period} onChange={(e) => setPeriod(e.target.value as RebatePeriod)} className={INPUT}>
              {(['month', 'quarter', 'year'] as RebatePeriod[]).map((p) => <option key={p} value={p}>{PERIOD_LABELS[p]}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ra-start-month" className={LABEL}>The year starts in</label>
            <select id="ra-start-month" value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} className={INPUT}>
              {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        </div>
      )}

      {kind !== 'settlement_discount' && (
        <label htmlFor="ra-delivery" className="flex items-start gap-2 text-sm text-neutral-800">
          <input id="ra-delivery" type="checkbox" className="mt-1 flex-shrink-0" checked={includeDelivery}
            onChange={(e) => setIncludeDelivery(e.target.checked)} />
          <span className="min-w-0 break-words">Delivery charges count towards spend</span>
        </label>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="ra-from" className={LABEL}>Starts</label>
          <input id="ra-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
        </div>
        <div>
          <label htmlFor="ra-to" className={LABEL}>Ends (optional)</label>
          <input id="ra-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={INPUT} />
        </div>
      </div>

      <div>
        <label htmlFor="ra-notes" className={LABEL}>Notes (optional)</label>
        <textarea id="ra-notes" rows={2} value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} className={INPUT} />
      </div>

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>
      )}
    </Dialog>
  );
}

export default RebateAgreementDialog;
